import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, ShoppingCart, Clock, Truck, AlertTriangle, Coins, Loader2, Package, BarChart3, Users, Settings, BookOpen, ChevronRight, Star } from "lucide-react";
import { api } from "../../lib/api";
import { fcfa } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { PointageCard } from "../../components/admin/PointageCard";
import { VendeurCommissionCard } from "../../components/admin/VendeurCommissionCard";
import { AdminVendeurCommissions } from "../../components/admin/AdminVendeurCommissions";
import { VendeurCaisseCard } from "../../components/admin/VendeurCaisseCard";
import { AdminCaisse } from "../../components/admin/AdminCaisse";
import { TopVendeurs } from "../../components/admin/TopVendeurs";
import { AdminAttendance } from "../../components/admin/AdminAttendance";

const QUICK_NAV = [
  { to: "/admin/commandes", label: "Ventes & commandes", desc: "Enregistrer / valider les ventes", icon: ShoppingCart, color: "bg-blue-100 text-blue-700", roles: ["patron", "vendeur", "associe"] },
  { to: "/admin/produits", label: "Stock & produits", desc: "Fiches, photos, vidéos, stock", icon: Package, color: "bg-brand/15 text-brand", roles: ["patron", "vendeur", "associe"] },
  { to: "/admin/avis", label: "Avis clients", desc: "Voir / ajouter / supprimer", icon: Star, color: "bg-yellow-100 text-yellow-700", roles: ["patron", "associe"] },
  { to: "/admin/livraisons", label: "Livraisons", desc: "Suivi des livraisons", icon: Truck, color: "bg-indigo-100 text-indigo-700", roles: ["patron", "vendeur", "associe"] },
  { to: "/admin/rapports", label: "Bénéfices & rapports", desc: "Chiffre d'affaires, marges", icon: BarChart3, color: "bg-emerald-100 text-emerald-700", roles: ["patron", "associe"] },
  { to: "/admin/ministere", label: "Ministère (dons)", desc: "Livres, vidéos, dons", icon: BookOpen, color: "bg-amber-100 text-amber-700", roles: ["patron"] },
  { to: "/admin/utilisateurs", label: "Utilisateurs", desc: "Vendeurs & associés", icon: Users, color: "bg-purple-100 text-purple-700", roles: ["patron"] },
  { to: "/admin/parametres", label: "Paiement & réglages", desc: "Numéros, zones, frais", icon: Settings, color: "bg-slate-100 text-slate-700", roles: ["patron"] },
];

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-brand-line rounded-xl p-5 hover:-translate-y-1 hover:shadow-md transition-all duration-300">
      <div className="flex items-center justify-between">
        <span className="text-sm text-brand-muted">{label}</span>
        <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent}`}><Icon className="w-5 h-5" /></span>
      </div>
      <p className="font-heading font-black text-2xl text-brand-ink mt-3">{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then(({ data }) => setData(data)).catch(() => setData(false));
  }, []);

  if (data === null) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand" /></div>;
  if (data === false) return <p className="text-brand-muted">Erreur de chargement.</p>;

  const quickNav = QUICK_NAV.filter((n) => n.roles.includes(user?.role));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-black tracking-tight text-brand-ink">Bonjour, {user?.name?.split(" ")[0]} 👋</h1>
        <p className="text-brand-muted mt-1">Voici l'activité de Noumabou en temps réel.</p>
      </div>

      {user?.role === "vendeur" && <PointageCard />}
      {user?.role === "vendeur" && <VendeurCommissionCard />}
      {user?.role === "vendeur" && <VendeurCaisseCard />}
      {(user?.role === "patron" || user?.role === "associe") && <AdminVendeurCommissions />}
      {(user?.role === "patron" || user?.role === "associe") && <AdminAttendance />}
      {(user?.role === "patron" || user?.role === "associe") && <AdminCaisse />}
      {(user?.role === "patron" || user?.role === "associe") && <TopVendeurs />}

      {/* Accès rapide à la gestion */}
      <div>
        <h2 className="font-heading font-bold text-brand-ink mb-3">Gérer ma boutique</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {quickNav.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`quick-${n.to.split("/").pop()}`}
              className="group bg-white border border-brand-line rounded-2xl p-4 flex flex-col gap-2 hover:-translate-y-1 hover:shadow-lg hover:border-brand/40 transition-all duration-300">
              <span className={`w-11 h-11 rounded-xl flex items-center justify-center ${n.color}`}><n.icon className="w-6 h-6" /></span>
              <span className="font-heading font-bold text-brand-ink text-sm leading-tight mt-1">{n.label}</span>
              <span className="text-xs text-brand-muted leading-snug flex-1">{n.desc}</span>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand group-hover:gap-2 transition-all">Ouvrir <ChevronRight className="w-3.5 h-3.5" /></span>
            </Link>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-ca-day" icon={TrendingUp} label="CA aujourd'hui" value={fcfa(data.ca_day)} accent="bg-amber-100 text-amber-700" />
        <StatCard testid="stat-ca-week" icon={Coins} label="CA de la semaine" value={fcfa(data.ca_week)} accent="bg-lime-100 text-lime-700" />
        {data.can_see_month && (
          <StatCard testid="stat-ca-month" icon={Coins} label="CA du mois" value={fcfa(data.ca_month)} accent="bg-green-100 text-green-700" />
        )}
        <StatCard testid="stat-orders-day" icon={ShoppingCart} label="Ventes aujourd'hui" value={data.orders_day} accent="bg-blue-100 text-blue-700" />
        <StatCard testid="stat-orders-total" icon={ShoppingCart} label="Ventes totales" value={data.orders_total} accent="bg-slate-100 text-slate-700" />
      </div>

      {data.can_see_profit && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard testid="stat-profit-day" icon={TrendingUp} label="Bénéfice aujourd'hui" value={fcfa(data.profit_day)} accent="bg-emerald-100 text-emerald-700" />
          <StatCard testid="stat-profit-week" icon={TrendingUp} label="Bénéfice semaine" value={fcfa(data.profit_week)} accent="bg-emerald-100 text-emerald-700" />
          <StatCard testid="stat-profit-month" icon={TrendingUp} label="Bénéfice du mois" value={fcfa(data.profit_month)} accent="bg-emerald-100 text-emerald-700" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <StatCard testid="stat-pending-payments" icon={Clock} label="Paiements en attente" value={data.pending_payments} accent="bg-orange-100 text-orange-700" />
        <StatCard testid="stat-to-deliver" icon={Truck} label="À livrer" value={data.to_deliver} accent="bg-blue-100 text-blue-700" />
        <StatCard testid="stat-low-stock" icon={AlertTriangle} label="Stock bas" value={data.low_stock_count} accent="bg-red-100 text-red-700" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-brand-line rounded-xl p-6">
          <h3 className="font-heading font-semibold text-brand-ink mb-4">Produits les plus vendus</h3>
          {data.top_products.length === 0 ? (
            <p className="text-sm text-brand-muted">Aucune vente pour l'instant.</p>
          ) : (
            <div className="space-y-3">
              {data.top_products.map((p, i) => (
                <div key={i} className="flex items-center justify-between" data-testid={`top-product-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-lg bg-brand-cream text-brand-ink font-semibold flex items-center justify-center text-sm">{i + 1}</span>
                    <span className="text-sm text-brand-ink">{p.name}</span>
                  </div>
                  <span className="text-sm font-semibold text-brand-sub">{p.qty} vendus</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-brand-line rounded-xl p-6">
          <h3 className="font-heading font-semibold text-brand-ink mb-4">Alertes stock bas</h3>
          {data.low_stock.length === 0 ? (
            <p className="text-sm text-brand-muted">Tout le stock est au vert ✅</p>
          ) : (
            <div className="space-y-3">
              {data.low_stock.map((p) => (
                <div key={p.id} className="flex items-center justify-between" data-testid={`low-stock-${p.id}`}>
                  <span className="text-sm text-brand-ink">{p.name}</span>
                  <span className="text-sm font-semibold text-brand-secondary">{p.stock_qty} restant(s)</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
